import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export const About = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const textRefs = useRef([]);
  const statsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.from(titleRef.current, {
        x: -100,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: titleRef.current,
          start: "top 80%",
          end: "top 50%",
          toggleActions: "play none none reverse"
        }
      });

      // Text paragraphs stagger
      textRefs.current.forEach((ref, index) => {
        gsap.from(ref, {
          y: 60,
          opacity: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ref,
            start: "top 85%",
            toggleActions: "play none none reverse"
          },
          delay: index * 0.1
        });
      });

      // Stats counter animation
      const stats = statsRef.current?.querySelectorAll('.stat-number');
      stats?.forEach((stat) => {
        const value = stat.getAttribute('data-value');
        gsap.from(stat, {
          textContent: 0,
          duration: 2,
          ease: "power2.out",
          snap: { textContent: 1 },
          scrollTrigger: {
            trigger: stat,
            start: "top 85%",
            toggleActions: "play none none reverse"
          },
          onUpdate: function() {
            if (value === "20") {
              stat.textContent = Math.ceil(this.targets()[0].textContent) + "+";
            }
          }
        });
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const addToRefs = (el) => {
    if (el && !textRefs.current.includes(el)) {
      textRefs.current.push(el);
    }
  };

  return (
    <section
      ref={sectionRef}
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 bg-background border-t border-border/50 overflow-hidden"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Left Column */}
          <div ref={titleRef} className="md:col-span-4">
            <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-medium block mb-4">
              01
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground">
              About
            </h2>
          </div>

          {/* Right Column */}
          <div className="md:col-span-8 space-y-8">
            <p ref={addToRefs} className="text-lg md:text-xl text-foreground leading-relaxed font-light">
              Technology executive with 20+ years of experience designing, deploying and operating 
              complex technical infrastructures across Europe, Africa, Australia, the Middle East and Southeast Asia.
            </p>

            <p ref={addToRefs} className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Expert in systems architecture, Apple ecosystem infrastructure, cybersecurity and 
              operational technology environments, with a proven ability to build scalable systems 
              and lead international technology operations.
            </p>

            <p ref={addToRefs} className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Former Apple CEMEA Africa Area Manager, leading regional expansion and technology 
              ecosystem deployment. Founder of Mac Expert, providing advanced Apple infrastructure 
              consulting and cybersecurity services.
            </p>

            <p ref={addToRefs} className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Today, based in Bali, leading FIRST Group — a technology-driven real estate and 
              operational infrastructure group serving international investors.
            </p>

            {/* Stats */}
            <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-8 border-t border-border/50">
              <div className="space-y-2">
                <span className="font-heading text-3xl md:text-4xl font-bold text-foreground">20+</span>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Years Experience</p>
              </div>
              <div className="space-y-2">
                <span className="font-heading text-3xl md:text-4xl font-bold text-foreground">5</span>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Continents</p>
              </div>
              <div className="space-y-2">
                <span className="font-heading text-3xl md:text-4xl font-bold text-foreground">Apple</span>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">CEMEA Expert</p>
              </div>
              <div className="space-y-2">
                <span className="font-heading text-3xl md:text-4xl font-bold text-foreground">Bali</span>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Based</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
